import React from "react";
import styled from "styled-components";
import { Icon as VendoredIcon, IconName as VendoredIconName } from "components/ui";
import { IconName, iconRegistry } from "./registry";
import { rendersFromVendoredSet } from "./legacy";

/**
 * `default` — inherits its label's colour. Everything: tool buttons, tabs, chevrons.
 * `action`  — purple. Buttons that import, save, export, add. The one place an icon
 *             breaks from its label, and what marks a button as an *action* rather
 *             than a *tool* (§6).
 * `muted`   — secondary text colour, for decorative or disabled glyphs.
 * `on-primary` / `on-secondary` — the label colour of a lime / purple fill.
 */
export type IconTone = "default" | "action" | "muted" | "on-primary" | "on-secondary";

/**
 * Legacy colour names from `components/ui`'s Icon. Present only so a call site can
 * migrate by changing its import; prefer `tone`, or let the icon inherit.
 */
export type LegacyIconColor =
	| "primary"
	| "secondary"
	| "white"
	| "black"
	| "red"
	| "grey900"
	| "grey800"
	| "grey700"
	| "grey600"
	| "grey500"
	| "grey400"
	| "grey300"
	| "grey200"
	| "grey100"
	| "grey50"
	| "grey30";

export interface IconProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
	name: IconName;
	tone?: IconTone;
	/** Any CSS length. Defaults to the 24px token; §6 wants 24 nearly everywhere. */
	size?: string;
	/** @deprecated Pass `tone`, or set `color` on the parent and let the icon inherit. */
	color?: LegacyIconColor;
	/** @deprecated Hover colour belongs to the parent's `:hover`, not the icon. */
	hoverColor?: LegacyIconColor;
	/**
	 * Which icon set to draw from. Defaults to `auto`: the old glyph while this
	 * name is still listed in `legacy.ts`, the new one once it graduates.
	 *
	 * `hds` forces the new set. Use it only where a surface is new enough that
	 * the old set has no glyph for part of it — a panel mixing filled old icons
	 * with outline new ones reads worse than either set on its own.
	 */
	set?: "auto" | "hds";
}

const legacyColorToken: Record<LegacyIconColor, string> = {
	primary: "var(--color-primary)",
	secondary: "var(--color-secondary)",
	white: "var(--color-surface)",
	black: "var(--color-text)",
	red: "var(--color-danger)",
	grey900: "var(--color-text)",
	grey800: "var(--color-text-secondary)",
	grey700: "var(--color-text-secondary)",
	grey600: "var(--color-text-secondary)",
	grey500: "var(--color-text-secondary)",
	grey400: "var(--color-border)",
	grey300: "var(--color-border)",
	grey200: "var(--color-grey-100)",
	grey100: "var(--color-grey-100)",
	grey50: "var(--color-grey-50)",
	grey30: "var(--color-grey-100)",
};

const toneToken: Record<IconTone, string> = {
	default: "currentColor",
	action: "var(--color-secondary)",
	muted: "var(--color-text-secondary)",
	"on-primary": "var(--color-text-on-primary)",
	"on-secondary": "var(--color-text-on-secondary)",
};

/**
 * DESIGN_SYSTEM.md §6: 24px, outline, 1.8px stroke, round caps and joins,
 * `currentColor`.
 *
 * The set this replaces had none of that — 117 hand-drawn filled glyphs with hex
 * fills baked into each path and no `currentColor` anywhere, which made §6's
 * colour rules literally unimplementable. Lucide is 24×24, `currentColor`, round
 * caps and joins natively; the stroke width is the only thing we set.
 */
const Icon = ({ name, tone = "default", size, color, hoverColor, set = "auto", ...rest }: IconProps) => {
	// §6 migration: most names still draw from the vendored set. It has its own
	// sizing and its fills are baked into each path, so it is rendered as the call
	// sites did before Phase 4 — directly, with no 24px slot forced around it and
	// no `tone`, which a glyph that cannot inherit currentColor could not honour.
	if (set === "auto" && rendersFromVendoredSet(name)) {
		return (
			<VendoredIcon
				name={name as VendoredIconName}
				size={size}
				color={color}
				hoverColor={hoverColor}
				aria-hidden
				{...rest}
			/>
		);
	}

	const Glyph = iconRegistry[name];

	if (!Glyph) {
		// A missing key is a compile error, so this only fires if the registry is
		// edited at runtime. Render nothing rather than a broken box.
		return null;
	}

	const resolved = color ? legacyColorToken[color] : toneToken[tone];

	return (
		<Slot $color={resolved} $size={size} aria-hidden {...rest} className={`hds-icon-slot${rest.className ? ` ${rest.className}` : ""}`}>
			<Glyph absoluteStrokeWidth={false} />
		</Slot>
	);
};

const Slot = styled.span<{ $color: string; $size?: string }>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: ${({ $size }) => $size ?? "var(--size-icon)"};
	height: ${({ $size }) => $size ?? "var(--size-icon)"};
	color: ${({ $color }) => $color};

	svg {
		width: 100%;
		height: 100%;
		stroke-width: var(--size-icon-stroke);
	}
`;

export default Icon;
